/**
 * Decision logic for the "this might already exist" dialog (backlog A5).
 *
 * This is a plain `.ts` util and not part of the component ON PURPOSE - backlog B14.
 * `frontend/vitest.config.ts` sets `environment: 'node'` AND `include: ['src/**\/*.test.ts']`,
 * and there is no jsdom/happy-dom/testing-library in this project, so a `.test.tsx` is never
 * even COLLECTED - it would silently never run, which is worse than having no test. Anything
 * that lives inside the dialog component is therefore unverifiable. Keep this logic here.
 *
 * Mirrors `DuplicateCandidate` in backend/src/services/propertyDuplicateService.ts. There is
 * no shared-types package wired between frontend and backend in this repo, so the two are kept
 * in sync by hand.
 */

export type DuplicateMatchReason = 'address' | 'apn' | 'master_property';

export interface DuplicateCandidate {
  id: string;
  address: string | null;
  city: string | null;
  apn: string | null;
  matched_on: readonly DuplicateMatchReason[];
  has_analysis: boolean;
  document_count: number;
  is_archived: boolean;
}

export type DuplicateAction = 'open' | 'attach' | 'create_separate';

const REASON_LABEL: Record<DuplicateMatchReason, string> = {
  address: 'address',
  apn: 'APN',
  master_property: 'the same database record',
};

/**
 * True ONLY for the 409 that carries duplicate candidates.
 *
 * The check can also fail outright, and the server answers that with
 * `503 { error: 'duplicate_check_unavailable' }`. That is a broken check, NOT a duplicate, and
 * it must not reach the dialog: offering "create separately" in response to an outage would
 * hand the user a duplicate row on purpose - the exact thing A5 exists to stop. Matching on
 * BOTH the status and the error string is what keeps the two apart, so neither half is
 * redundant.
 *
 * Reads `body`, not `message`, deliberately: the candidate list only exists on the parsed
 * body, so a caller that has a truthy answer here is guaranteed to have something to render.
 *
 * The index signature on `body` is load-bearing, not decoration. The real 409 body is
 * `{ success, error, message, candidates }`, and without it TypeScript's excess-property check
 * rejects an inline literal of the ACTUAL server response (TS2353) - which fails `npm run
 * build` (`tsc && vite build`) while vitest, which strips types rather than checking them,
 * still reports green.
 */
export const isDuplicateConflict = (
  err: { status?: number; body?: { error?: string; [key: string]: unknown } } | null | undefined
): boolean => err?.status === 409 && err?.body?.error === 'possible_duplicate';

/** The 409 body the backend sends: `{ success, error, message, candidates }`. */
export interface DuplicateConflictBody {
  error?: string;
  candidates?: DuplicateCandidate[];
  [key: string]: unknown;
}

/**
 * The one entry point a page should use: hand it whatever was thrown, get back either the
 * candidates to put in the dialog or `null` meaning "not a duplicate - handle it as an
 * ordinary error".
 *
 * It exists so the three call sites do not each cast `err.body` to `any` and reach for
 * `.candidates` themselves. That would put three copies of an unverifiable assumption inside
 * three React components, where nothing in this repo can test them (B14).
 *
 * `null` for an EMPTY or missing list is deliberate and is not the same as an empty dialog.
 * A 409 with nothing to choose between would render a prompt whose only working button is
 * Cancel - a dead end that silently loses the user's upload. Falling back to the ordinary
 * error path at least tells them something failed. The predicate above stays separate and
 * unchanged: it is what keeps the 503 (a BROKEN check, not a duplicate) out of here, so no
 * outage can ever reach the "create separately" button.
 */
export const duplicateCandidatesFrom = (err: unknown): DuplicateCandidate[] | null => {
  const e = err as { status?: number; body?: DuplicateConflictBody } | null | undefined;
  if (!isDuplicateConflict(e)) return null;
  const candidates = e?.body?.candidates;
  return Array.isArray(candidates) && candidates.length > 0 ? candidates : null;
};

/**
 * One line that makes the choice decidable.
 *
 * `has_analysis` and `document_count` are the whole point: they are what says which of two
 * near-identical rows is the one with work already on it (A4 found a Bear Valley pair where
 * one row had a saved analysis and the other did not, so the rows were NOT interchangeable).
 * `is_archived` is called out because an archived row is invisible everywhere else in the app -
 * without this the user is told they have a duplicate of something they cannot see.
 */
export const summarizeCandidate = (c: DuplicateCandidate): string => {
  const where = [c.address, c.city].filter(Boolean).join(', ') || 'Address not recorded';
  const analysis = c.has_analysis ? 'saved analysis' : 'no saved analysis';
  const docs = c.document_count === 1 ? '1 document'
    : c.document_count === 0 ? 'no documents'
    : `${c.document_count} documents`;
  const reasons = c.matched_on.map((r) => REASON_LABEL[r]);
  const matched = reasons.length === 2 ? `${reasons[0]} and ${reasons[1]}`
    : reasons.length > 2 ? `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]}`
    : reasons[0] ?? 'unknown';
  return `${where} - ${analysis}, ${docs} - matched on ${matched}${c.is_archived ? ' (archived)' : ''}`;
};

/** What the dialog needs to know about the path it was opened from. */
export interface DuplicateDialogContext {
  /** Present only on the document-upload path. Its absence is what removes "Attach here". */
  documentId?: string;
}

/**
 * Which buttons the dialog offers, and - just as important - which it does NOT.
 *
 * Attach is offered only when there is actually an uploaded document to attach. The manual
 * "create property" paths (Dashboard, MasterPropertyDetail) have no document, so offering it
 * there would produce a button that cannot do anything.
 *
 * ⚠️ A BARE "OPEN EXISTING" IS WITHHELD WHENEVER THERE IS A DOCUMENT, and that is the whole
 * point of this branch. By the time the 409 fires on the upload path the PDF has been uploaded
 * AND run through Claude extraction (real API spend), and its `documents` row has
 * `property_id = null`. "Open existing" navigates to the property and leaves that row orphaned
 * FOREVER - there is no documents list anywhere in the frontend (`api.listDocuments` and
 * `api.getDocument` have zero callers), so nothing can ever find it again. That is precisely
 * the outcome the attach endpoint was built to prevent, and it was the loudest button in the
 * dialog. Attach lands on the SAME property and rescues the document, so it fully replaces
 * "open" here rather than competing with it.
 *
 * The two are therefore mutually exclusive by construction, which is what lets the dialog mark
 * both `open` and `attach` primary and still show exactly one primary button per row. That
 * invariant is asserted in the tests beside this file - the component itself cannot be tested
 * (B14).
 */
export const availableActions = (
  _candidate: DuplicateCandidate,
  ctx: DuplicateDialogContext
): DuplicateAction[] =>
  ctx.documentId ? ['attach', 'create_separate'] : ['open', 'create_separate'];

/**
 * The line under the dialog title, which has to be TRUE on the path it is shown on.
 *
 * "Nothing has been saved yet" is correct on the Dashboard and MasterPropertyDetail paths: the
 * duplicate check runs before any write, so cancelling really does leave nothing behind. It is
 * FALSE on the upload path, where the file is already stored and already extracted - and the
 * same branch's own Cancel copy (`pendingUploadNotice`) says so in as many words. Telling the
 * user nothing was saved and then, one click later, that their document "was uploaded and its
 * data extracted" is the dialog contradicting itself.
 *
 * The document-path sentence also has to say where the document ENDS UP, because both actions
 * still on offer put it somewhere: Attach re-homes it onto the existing property, and Create
 * separate attaches it to the newly created one.
 */
export const dialogSubtitle = (ctx: DuplicateDialogContext): string =>
  ctx.documentId
    ? 'Your document is already uploaded and its data extracted. No property has been created yet - the document will be attached to whichever property you choose.'
    : 'Nothing has been saved yet. Choose what to do.';

/**
 * Backend error CODES mapped to something a person can act on.
 *
 * `request()` in lib/api.ts sets `APIError.message = data.error`, so every one of these codes
 * was previously rendered verbatim in a red banner or a toast - the user was literally shown
 * `duplicate_check_unavailable`. The 503 was introduced precisely so the client could tell a
 * degraded READ path (the check did not run) apart from a broken WRITE path (the insert
 * failed); rendering the raw code throws that distinction away again.
 *
 * "nothing was created" is the load-bearing half of the 503 copy. A user who thinks the create
 * half-succeeded will go looking for the property, not retry.
 */
const ERROR_COPY: Record<string, string> = {
  duplicate_check_unavailable:
    'We could not check whether this property already exists, so nothing was created. Please try again shortly.',
  possible_duplicate:
    'This looks like a property you already have, but the matching records could not be loaded. Please try again shortly.',
};

/**
 * Machine codes are snake_case with no whitespace; no human-facing string in this app is.
 * Used to catch codes NOT in the table above, so the next one added on the backend degrades to
 * the fallback instead of leaking. Deliberately requires an underscore: a single bare word
 * ("Failed") is not treated as a code.
 */
const MACHINE_CODE = /^[a-z0-9]+(_[a-z0-9]+)+$/;

/**
 * The one place an error is turned into user-facing copy on the duplicate paths.
 *
 * Lives here rather than inline in the three pages for the same reason as everything else in
 * this file: `vitest.config.ts` cannot collect a `.test.tsx` (B14), so anything inside a
 * component is unverifiable.
 */
export const friendlyErrorMessage = (
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string => {
  const e = err as { message?: string; body?: { error?: string } } | null | undefined;
  // Prefer the parsed body: `message` is only a copy of `data.error`, and a caller that
  // rewrapped the error may have replaced it.
  const code = e?.body?.error;
  if (typeof code === 'string' && ERROR_COPY[code]) return ERROR_COPY[code];

  const message = typeof e?.message === 'string' ? e.message.trim() : '';
  if (!message) return fallback;
  if (ERROR_COPY[message]) return ERROR_COPY[message];
  return MACHINE_CODE.test(message) ? fallback : message;
};

/**
 * The id off a create response, or null - never a throw.
 *
 * All three create endpoints (`POST /properties`, `/properties/from-document/:id`,
 * `/properties/from-master/:id`) answer `{ success, message, property }`. Two call sites used
 * to read `result.property.id` unguarded, so ANY other shape threw into their catch and
 * reported "Failed to create property" for a property that had in fact been created. That is
 * the worst possible wrong message here: the user's response to it is to retry, which creates
 * the duplicate this whole feature exists to prevent.
 *
 * `{ success, data }` (what `attach-document` answers) deliberately returns null rather than
 * being accommodated - this reads the create shape, one way, and a caller handed the wrong
 * response should find out.
 */
export const propertyIdFrom = (result: unknown): string | null => {
  const r = result as { property?: { id?: unknown } | null } | null | undefined;
  const id = r?.property?.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

/**
 * An upload that reached the duplicate prompt: already stored, already extracted, not yet
 * attached to any property.
 */
export interface PendingUpload {
  documentId: string;
  fileName: string;
  fileSize: number;
  documentType: string;
}

/**
 * Whether a resubmit can reuse the document already uploaded, instead of starting over.
 *
 * By the time the 409 fires, the PDF has been uploaded AND run through Claude extraction. If
 * the user cancels the prompt and presses Submit again - the obvious next move - the naive
 * path re-uploads the same file, pays for a SECOND extraction of what may be a 100-page OM,
 * orphans a second `documents` row, and lands on the identical 409. Matching the file lets the
 * resubmit skip straight to the create.
 *
 * Name + size + type, not identity: `File` objects are recreated by the input element, so
 * there is no stable reference to compare. Any change to any of the three means the pending
 * document is no longer the thing being submitted, and reusing it would attach the wrong PDF -
 * so this fails closed and re-uploads.
 */
export const reusableDocumentId = (
  pending: PendingUpload | null | undefined,
  current: { fileName: string; fileSize: number; documentType: string }
): string | null => {
  if (!pending?.documentId) return null;
  const matches =
    pending.fileName === current.fileName &&
    pending.fileSize === current.fileSize &&
    pending.documentType === current.documentType;
  return matches ? pending.documentId : null;
};

/**
 * What to tell the user when they cancel the duplicate prompt.
 *
 * Cancel used to just close the dialog, leaving a form that looked untouched with the file
 * still selected and an orphaned `documents` row behind it. Both halves of this sentence are
 * needed: that the upload survived (so they do not think it vanished), and that answering
 * again costs nothing (so they do not avoid the feature to dodge a re-extraction).
 */
export const pendingUploadNotice = (pending: PendingUpload | null | undefined): string | null =>
  pending
    ? `"${pending.fileName}" was uploaded and its data extracted, but it is not attached to any property yet. Submit again to choose - it will not re-upload or re-extract the file.`
    : null;

/**
 * A synchronous single-flight lock for the dialog's actions.
 *
 * ⚠️ THE DIALOG'S `busy` PROP IS NOT ENOUGH, and this was proven against production rather
 * than reasoned about. `busy` is React state, and React batches state updates - so two clicks
 * landing in the SAME synchronous tick both observe `busy === false` and both fire their
 * handler. On 2026-08-06 two clicks on "Create separate" created TWO properties 2ms apart:
 * the feature whose entire purpose is preventing duplicate rows created one. The button did
 * become disabled, but only on the re-render about 120ms later, which is why an ordinary
 * human double-click happens to be caught and a faster one is not.
 *
 * `tryAcquire()` flips a plain closure variable with no scheduling in between, so the second
 * caller is refused in the same tick. Hold one per component in a `useRef` and always release
 * in a `finally`, or a failed action locks the button out for good.
 *
 * `busy` stays - it is what visibly disables the button. This is the correctness half.
 */
export interface SubmitLock {
  /** True if the caller now holds the lock. False means another call is already in flight. */
  tryAcquire(): boolean;
  release(): void;
}

export const createSubmitLock = (): SubmitLock => {
  let held = false;
  return {
    tryAcquire: () => {
      if (held) return false;
      held = true;
      return true;
    },
    release: () => {
      held = false;
    },
  };
};
